import * as React from "react";
import { createLogger } from "../../../utils/logger";

const log = createLogger("ConciseModeBadge");

/** Badge color - darker shade of send button color for visual weight on thin SVG lines */
const BADGE_COLOR = "#087199";
const BADGE_BG = "rgba(10, 140, 187, 0.15)";

interface ConciseModeBadgeProps {
  conciseMode: boolean;
  onToggle: () => void;
}

const ConciseModeIcon = ({ size = 16, color = BADGE_COLOR }: { size?: number; color?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 0.30791C19.759 0.263383 19.5514 0.271701 19.3869 0.497365C17.9778 2.43063 16.3657 4.21894 14.9023 6.11397C13.2895 8.20256 11.644 10.2688 9.6479 12.0544C7.85395 13.6591 5.85549 15.011 3.53738 15.8115C2.63808 16.122 2.20369 16.6007 1.84221 17.3404C1.42795 18.1881 4.75087 16.5713 4.1827 17.3404C3.13253 18.0302 0.229646 20.0179 0.0494217 19.9999C-0.0531409 19.8606 0.0263482 19.7351 0.086497 19.6156C1.64955 16.5094 2.78668 13.2399 4.1827 10.07C5.22648 7.69989 6.51069 5.46985 8.51351 3.67145C10.5506 1.84231 13.0044 0.833832 15.7194 0.317007C17.1004 0.0541404 18.4931 -0.0575392 19.9505 0.0286694C20 0.116371 20 0.192986 20 0.30791ZM11.8711 5.07044C10.2457 6.3792 8.81433 7.85366 7.62067 9.53021C6.66631 10.8707 5.72372 12.2187 4.75856 13.5521C4.31735 14.1617 3.83441 14.7442 3.37039 15.339C3.53209 15.337 3.64064 15.2866 3.74407 15.2282C6.99712 13.3889 9.72868 11.001 12.0524 8.18034C14.2301 5.53679 16.3554 2.85013 18.95 0.542735C18.9777 0.518137 18.9754 0.463476 18.9957 0.393277C17.3463 0.634753 15.7534 0.953318 14.2123 1.48823C11.5477 2.41309 9.29901 3.86709 7.58618 6.03593C5.66541 8.46807 4.6279 11.2996 3.47399 14.0817C3.55245 14.0155 3.60011 13.9333 3.65043 13.8526C4.76507 12.0654 5.85717 10.2639 7.14867 8.58267C8.37711 6.98347 9.8227 5.59729 11.6712 4.63259C11.9126 4.50659 12.1554 4.28579 12.5226 4.41649C12.4071 4.72514 12.1295 4.84136 11.8711 5.07044Z"
      fill={color}
    />
    <path
      d="M16.2148 17.2621V17.2631C16.2461 17.2759 16.263 17.296 16.2705 17.3138C16.2777 17.331 16.277 17.3475 16.2695 17.3646C15.7142 18.6088 14.8776 19.473 13.7236 19.473C12.8765 19.4728 12.2123 19.0148 11.6904 18.2767L11.2822 17.6996L10.874 18.2767C10.3522 19.0148 9.68876 19.4729 8.8418 19.473C7.99453 19.4729 7.3305 19.0149 6.80859 18.2767L6.40039 17.6996L5.99219 18.2767C5.47025 19.0149 4.80618 19.473 3.95898 19.473L3.90723 19.4633C3.86509 19.4456 3.84863 19.4076 3.84863 19.3871L3.85449 19.3627C3.86656 19.3348 3.89837 19.3021 3.95898 19.3021C4.94525 19.3021 5.7283 18.5668 6.2832 17.3373L6.30469 17.2963C6.32147 17.2744 6.35533 17.2533 6.40039 17.2533C6.44203 17.2534 6.47306 17.2718 6.49121 17.2924L6.50488 17.3129L6.50879 17.3217L6.60938 17.5345L6.61328 17.5433C7.15856 18.6459 7.91153 19.3017 8.84082 19.3021H8.8418C9.8343 19.3019 10.6223 18.5577 11.1777 17.3138L11.1846 17.3002C11.2003 17.277 11.2348 17.2534 11.2822 17.2533C11.3377 17.2533 11.3754 17.2853 11.3877 17.3129L11.3916 17.3217L11.4912 17.5345L11.4961 17.5433C12.0414 18.646 12.7942 19.3019 13.7236 19.3021C14.7166 19.3021 15.505 18.5574 16.0605 17.3129C16.069 17.294 16.0864 17.274 16.1172 17.2621C16.148 17.2502 16.1832 17.2494 16.2148 17.2621Z"
      fill={color}
      stroke={color}
    />
  </svg>
);

const ConciseModeBadge: React.FC<ConciseModeBadgeProps> = ({ conciseMode, onToggle }) => {
  const [isPressed, setIsPressed] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  if (!conciseMode) return null;

  const handleClick = () => {
    log.info("Concise Mode badge clicked - toggling mode", {
      currentState: conciseMode,
      willChangeTo: !conciseMode,
    });

    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 150);

    onToggle();
  };

  return (
    <div
      title="Concise Writing Style active - click to disable"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        backgroundColor: BADGE_BG,
        cursor: "pointer",
        transition: "all 0.2s ease",
        transform: isPressed ? "scale(0.9)" : "scale(1)",
      }}
    >
      {/* Concise Mode Icon - hidden on hover */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isHovered ? 0 : 1,
          transition: "opacity 0.15s ease",
        }}
      >
        <ConciseModeIcon size={16} color={BADGE_COLOR} />
      </div>

      {/* X overlay on hover */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          backgroundColor: BADGE_BG,
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.15s ease",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5"
            stroke={BADGE_COLOR}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>
  );
};

export default ConciseModeBadge;
